import Icon from './Icon'
import DonutGauge from './DonutGauge'
import styles from './StatBlock.module.css'

/**
 * StatBlock — renders a `stat` content block: a big value with an optional
 * unit, caption, secondary sub-caption, a neutral trend delta, and either a
 * decorative icon or a large DonutGauge on the trailing edge.
 *
 * Used two ways:
 *   - standalone, as an insight card's hero stat (full size, optional icon/gauge)
 *   - repeated inside a `statGrid` block (the 4-tile row in the detail modal),
 *     via `tile` — smaller, centered, no icon/gauge/delta clutter.
 *
 * Props mirror the `stat` block schema:
 *   value {string|number}
 *   unit {string}
 *   caption {string}
 *   subCaption {string}
 *   delta {{direction:'up'|'down', label?:string}}
 *   icon {string} — Icon key; ignored if `gauge` is set. Rendered in a
 *                   bordered, accent-tinted box on the trailing edge.
 *   gauge {{value,max,centerValue,color}} — renders a DonutGauge instead of
 *                   an icon, also on the trailing edge.
 *   accentColor {string} — CSS color for the value text, the gauge ring (unless
 *                   the gauge carries its own color), and (for the icon variant
 *                   only) the icon's border/background tint
 *   tile {boolean} — compact statGrid-tile styling
 */
export default function StatBlock({
  value, unit, caption, subCaption, delta, icon, gauge, accentColor, tile = false,
}) {
  return (
    <div className={`${styles.stat} ${tile ? styles.tile : ''}`}>
      <div className={styles.main}>
        <div className={styles.valueRow}>
          <span className={styles.value} style={accentColor ? { color: accentColor } : undefined}>{value}</span>
          {unit && <span className={styles.unit}>{unit}</span>}
        </div>
        {caption && <div className={styles.caption}>{caption}</div>}
        {subCaption && <div className={styles.subCaption}>{subCaption}</div>}
        {delta && (
          <div className={styles.delta}>
            <span className={styles.deltaArrow} aria-hidden="true">{delta.direction === 'down' ? '▼' : '▲'}</span>
            {delta.label && <span>{delta.label}</span>}
          </div>
        )}
      </div>

      {!tile && (gauge || icon) && (
        <div className={styles.side}>
          {gauge
            ? <DonutGauge size={79} strokeWidth={9} centerValueSize="1.3rem" {...gauge} color={gauge.color ?? accentColor} />
            : (
              <span
                className={styles.icon}
                style={accentColor ? {
                  color: accentColor,
                  borderColor: `color-mix(in srgb, ${accentColor} 45%, transparent)`,
                  backgroundColor: `color-mix(in srgb, ${accentColor} 16%, transparent)`,
                } : undefined}
              >
                <Icon name={icon} />
              </span>
            )}
        </div>
      )}
    </div>
  )
}
